import { collection, getDocs, query, limit } from 'firebase/firestore';
import { db } from './config';

export interface SearchResult {
  id: string;
  type: 'users' | 'posts' | 'groups' | 'events' | 'marketplace' | 'music' | 'hotels' | 'apartments' | 'pages' | 'restaurants' | 'recipes';
  title: string;
  subtitle?: string;
  image?: string;
  url: string;
  category?: string;
  relevanceScore?: number;
}

function normalizeForSearch(value: string): string {
  return value
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/\s+/g, ' ')
    .trim();
}

function levenshteinDistance(a: string, b: string): number {
  if (a === b) return 0;
  if (!a.length) return b.length;
  if (!b.length) return a.length;

  const matrix: number[][] = Array.from({ length: a.length + 1 }, () => new Array(b.length + 1).fill(0));

  for (let i = 0; i <= a.length; i += 1) matrix[i][0] = i;
  for (let j = 0; j <= b.length; j += 1) matrix[0][j] = j;

  for (let i = 1; i <= a.length; i += 1) {
    for (let j = 1; j <= b.length; j += 1) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      matrix[i][j] = Math.min(
        matrix[i - 1][j] + 1,
        matrix[i][j - 1] + 1,
        matrix[i - 1][j - 1] + cost
      );
    }
  }

  return matrix[a.length][b.length];
}

function fuzzyTokenMatch(queryToken: string, candidateToken: string): boolean {
  if (!queryToken || !candidateToken) return false;
  if (candidateToken.includes(queryToken) || candidateToken.startsWith(queryToken)) return true;

  const maxDistance = queryToken.length <= 4 ? 1 : 2;
  return levenshteinDistance(queryToken, candidateToken) <= maxDistance;
}

function scoreField(query: string, value: string): number {
  const normalizedValue = normalizeForSearch(value);
  const normalizedQuery = normalizeForSearch(query);

  if (!normalizedValue || !normalizedQuery) return 0;

  if (normalizedValue === normalizedQuery) return 120;
  if (normalizedValue.startsWith(normalizedQuery)) return 95;
  if (normalizedValue.includes(normalizedQuery)) return 75;

  const queryTokens = normalizedQuery.split(' ').filter(Boolean);
  const valueTokens = normalizedValue.split(/[^a-z0-9]+/).filter(Boolean);
  let tokenScore = 0;

  for (const token of queryTokens) {
    if (valueTokens.includes(token)) {
      tokenScore += 24;
      continue;
    }
    if (valueTokens.some((candidate) => candidate.startsWith(token))) {
      tokenScore += 18;
      continue;
    }
    if (valueTokens.some((candidate) => fuzzyTokenMatch(token, candidate))) {
      tokenScore += 12;
    }
  }

  if (tokenScore > 0) return tokenScore;

  const shortValue = normalizedValue.slice(0, Math.max(normalizedQuery.length + 2, 12));
  const distance = levenshteinDistance(normalizedQuery, shortValue);
  if (distance <= 2) {
    return 26 - distance * 5;
  }

  return 0;
}

function matchesSearchQuery(queryValue: string, fields: Array<string | undefined>): boolean {
  const best = fields.reduce((max, field) => {
    if (!field) return max;
    return Math.max(max, scoreField(queryValue, field));
  }, 0);

  return best >= 18;
}

function computeRelevanceScore(searchQuery: string, result: SearchResult): number {
  const titleScore = scoreField(searchQuery, result.title) * 1.3;
  const subtitleScore = scoreField(searchQuery, result.subtitle || '') * 0.9;
  const categoryScore = scoreField(searchQuery, result.category || '') * 0.5;
  const urlScore = scoreField(searchQuery, result.url) * 0.2;
  return Math.round(titleScore + subtitleScore + categoryScore + urlScore);
}

export async function searchContent(searchTerm: string, type: string = 'all'): Promise<SearchResult[]> {
  if (!searchTerm.trim() || searchTerm.length < 2) return [];

  try {
    const searchTermLower = normalizeForSearch(searchTerm);
    const results: SearchResult[] = [];

    // Search users if type is 'all' or 'users'
    if (type === 'all' || type === 'users') {
      try {
        const usersRef = collection(db, 'users');
        const usersSnapshot = await getDocs(query(usersRef, limit(50)));
        
        usersSnapshot.docs.forEach(doc => {
          const userData = doc.data();
          const name = (userData.name || userData.displayName || '').toLowerCase();
          const username = (userData.username || '').toLowerCase();
          const email = (userData.email || '').toLowerCase();
          
          if (matchesSearchQuery(searchTermLower, [name, username, email])) {
            results.push({
              id: doc.id,
              type: 'users',
              title: userData.name || userData.displayName || 'Unknown User',
              subtitle: `@${userData.username || userData.email?.split('@')[0] || ''}`,
              image: userData.photoURL,
              url: `/profile/${userData.username || doc.id}`,
              category: 'People'
            });
          }
        });
      } catch (error) {
        console.error('Error searching users:', error);
      }
    }

    // Search groups if type is 'all' or 'groups'
    if (type === 'all' || type === 'groups') {
      try {
        const groupsRef = collection(db, 'groups');
        const groupsSnapshot = await getDocs(query(groupsRef, limit(50)));
        
        groupsSnapshot.docs.forEach(doc => {
          const groupData = doc.data();
          const groupName = (groupData.name || '').toLowerCase();
          const description = (groupData.description || '').toLowerCase();
          
          if (matchesSearchQuery(searchTermLower, [groupName, description])) {
            results.push({
              id: doc.id,
              type: 'groups',
              title: groupData.name || 'Unnamed Group',
              subtitle: `${groupData.memberCount || 0} members • ${groupData.privacy || 'Public'}`,
              image: groupData.coverPhoto || groupData.image,
              url: `/group/${groupData.slug || doc.id}`,
              category: 'Groups'
            });
          }
        });
      } catch (error) {
        console.error('Error searching groups:', error);
      }
    }

    // Search marketplace products if type is 'all' or 'marketplace'
    if (type === 'all' || type === 'marketplace') {
      try {
        const productsRef = collection(db, 'products');
        const productsSnapshot = await getDocs(query(productsRef, limit(50)));
        
        productsSnapshot.docs.forEach(doc => {
          const productData = doc.data();
          const productName = (productData.name || '').toLowerCase();
          const description = (productData.description || '').toLowerCase();
          const category = (productData.category || '').toLowerCase();
          
          if (matchesSearchQuery(searchTermLower, [productName, description, category])) {
            results.push({
              id: doc.id,
              type: 'marketplace',
              title: productData.name || 'Unnamed Product',
              subtitle: `€${productData.price || 0} • ${productData.condition || 'New'}`,
              image: productData.images?.[0] || productData.image,
              url: `/product/${productData.slug || doc.id}`,
              category: 'Marketplace'
            });
          }
        });
      } catch (error) {
        console.error('Error searching marketplace:', error);
      }
    }

    // Search hotels if type is 'all' or 'hotels'
    if (type === 'all' || type === 'hotels') {
      try {
        const hotelsRef = collection(db, 'hotels');
        const hotelsSnapshot = await getDocs(query(hotelsRef, limit(30)));
        
        hotelsSnapshot.docs.forEach(doc => {
          const hotelData = doc.data();
          const hotelName = (hotelData.hotelName || hotelData.name || '').toLowerCase();
          const description = (hotelData.description || '').toLowerCase();
          const location = (hotelData.location?.address || '').toLowerCase();
          
          if (matchesSearchQuery(searchTermLower, [hotelName, description, location])) {
            results.push({
              id: doc.id,
              type: 'hotels',
              title: hotelData.hotelName || hotelData.name || 'Unnamed Hotel',
              subtitle: hotelData.location?.address || 'Location not specified',
              image: hotelData.images?.[0],
              url: `/booking/${hotelData.slug || doc.id}`,
              category: 'Hotels'
            });
          }
        });
      } catch (error) {
        console.error('Error searching hotels:', error);
      }
    }

    // Search apartments if type is 'all' or 'apartments'
    if (type === 'all' || type === 'apartments') {
      try {
        const apartmentsRef = collection(db, 'apartments');
        const apartmentsSnapshot = await getDocs(query(apartmentsRef, limit(30)));
        
        apartmentsSnapshot.docs.forEach(doc => {
          const apartmentData = doc.data();
          const apartmentName = (apartmentData.apartmentName || apartmentData.name || '').toLowerCase();
          const description = (apartmentData.description || '').toLowerCase();
          const location = (apartmentData.location?.address || '').toLowerCase();
          
          if (matchesSearchQuery(searchTermLower, [apartmentName, description, location])) {
            results.push({
              id: doc.id,
              type: 'apartments',
              title: apartmentData.apartmentName || apartmentData.name || 'Unnamed Apartment',
              subtitle: apartmentData.location?.address || 'Location not specified',
              image: apartmentData.images?.[0],
              url: `/booking/${apartmentData.slug || doc.id}`,
              category: 'Apartments'
            });
          }
        });
      } catch (error) {
        console.error('Error searching apartments:', error);
      }
    }

    // Search music tracks if type is 'all' or 'music'
    if (type === 'all' || type === 'music') {
      try {
        const tracksRef = collection(db, 'music');
        const tracksSnapshot = await getDocs(query(tracksRef, limit(30)));
        
        tracksSnapshot.docs.forEach(doc => {
          const trackData = doc.data();
          const trackTitle = (trackData.title || '').toLowerCase();
          const artist = (trackData.artist || '').toLowerCase();
          const album = (trackData.album || '').toLowerCase();
          
          if (matchesSearchQuery(searchTermLower, [trackTitle, artist, album])) {
            results.push({
              id: doc.id,
              type: 'music',
              title: trackData.title || 'Unknown Track',
              subtitle: `${trackData.artist || 'Unknown Artist'}${trackData.album ? ' • ' + trackData.album : ''}`,
              image: trackData.coverArt || trackData.albumArt,
              url: `/music?track=${doc.id}`,
              category: 'Music'
            });
          }
        });
      } catch (error) {
        console.error('Error searching music:', error);
      }
    }

    // Search events/tickets if type is 'all' or 'events'
    if (type === 'all' || type === 'events') {
      try {
        const eventsRef = collection(db, 'tickets');
        const eventsSnapshot = await getDocs(query(eventsRef, limit(30)));
        
        eventsSnapshot.docs.forEach(doc => {
          const eventData = doc.data();
          const eventName = (eventData.eventName || eventData.name || '').toLowerCase();
          const description = (eventData.description || '').toLowerCase();
          const location = (eventData.location || '').toLowerCase();
          
          if (matchesSearchQuery(searchTermLower, [eventName, description, location])) {
            results.push({
              id: doc.id,
              type: 'events',
              title: eventData.eventName || eventData.name || 'Unnamed Event',
              subtitle: `${eventData.date ? new Date(eventData.date).toLocaleDateString() : 'Date TBA'} • ${eventData.location || 'Location TBA'}`,
              image: eventData.image || eventData.coverImage,
              url: `/tickets?event=${doc.id}`,
              category: 'Events'
            });
          }
        });
      } catch (error) {
        console.error('Error searching events:', error);
      }
    }

    // Search pages if type is 'all' or 'pages'
    if (type === 'all' || type === 'pages') {
      try {
        const pagesRef = collection(db, 'pages');
        const pagesSnapshot = await getDocs(query(pagesRef, limit(30)));
        
        pagesSnapshot.docs.forEach(doc => {
          const pageData = doc.data();
          const pageName = (pageData.name || '').toLowerCase();
          const description = (pageData.description || '').toLowerCase();
          const category = (pageData.category || '').toLowerCase();
          
          if (matchesSearchQuery(searchTermLower, [pageName, description, category])) {
            results.push({
              id: doc.id,
              type: 'pages',
              title: pageData.name || 'Unnamed Page',
              subtitle: `${pageData.category || 'Page'} • ${pageData.followers?.length || 0} followers`,
              image: pageData.avatar || pageData.image,
              url: `/page/${pageData.slug || doc.id}`,
              category: 'Pages'
            });
          }
        });
      } catch (error) {
        console.error('Error searching pages:', error);
      }
    }

    // Search restaurants if type is 'all' or 'restaurants'
    if (type === 'all' || type === 'restaurants') {
      try {
        const restaurantsRef = collection(db, 'restaurants');
        const restaurantsSnapshot = await getDocs(query(restaurantsRef, limit(30)));
        
        restaurantsSnapshot.docs.forEach(doc => {
          const restaurantData = doc.data();
          const restaurantName = (restaurantData.name || '').toLowerCase();
          const description = (restaurantData.description || '').toLowerCase();
          const cuisine = (restaurantData.cuisine || '').toLowerCase();
          
          if (matchesSearchQuery(searchTermLower, [restaurantName, description, cuisine])) {
            results.push({
              id: doc.id,
              type: 'restaurants',
              title: restaurantData.name || 'Unnamed Restaurant',
              subtitle: `${restaurantData.cuisine || 'Restaurant'} • ${restaurantData.rating || 'No rating'}⭐`,
              image: restaurantData.image || restaurantData.logo,
              url: `/food?restaurant=${doc.id}`,
              category: 'Restaurants'
            });
          }
        });
      } catch (error) {
        console.error('Error searching restaurants:', error);
      }
    }

    // Search recipes if type is 'all' or 'recipes'
    if (type === 'all' || type === 'recipes') {
      try {
        const recipesRef = collection(db, 'recipes');
        const recipesSnapshot = await getDocs(query(recipesRef, limit(30)));
        
        recipesSnapshot.docs.forEach(doc => {
          const recipeData = doc.data();
          const recipeName = (recipeData.title || recipeData.name || '').toLowerCase();
          const description = (recipeData.description || '').toLowerCase();
          const ingredients = (recipeData.ingredients || []).join(' ').toLowerCase();
          
          if (matchesSearchQuery(searchTermLower, [recipeName, description, ingredients])) {
            results.push({
              id: doc.id,
              type: 'recipes',
              title: recipeData.title || recipeData.name || 'Unnamed Recipe',
              subtitle: `${recipeData.prepTime || 0} min • ${recipeData.category || 'Recipe'}`,
              image: recipeData.image || recipeData.thumbnail,
              url: `/recipes?recipe=${doc.id}`,
              category: 'Recipes'
            });
          }
        });
      } catch (error) {
        console.error('Error searching recipes:', error);
      }
    }

    const dedupedByKey = new Map<string, SearchResult>();
    for (const result of results) {
      const key = `${result.type}:${result.id}`;
      const scored: SearchResult = {
        ...result,
        relevanceScore: computeRelevanceScore(searchTermLower, result),
      };

      const existing = dedupedByKey.get(key);
      if (!existing || (scored.relevanceScore || 0) > (existing.relevanceScore || 0)) {
        dedupedByKey.set(key, scored);
      }
    }

    const rankedResults = Array.from(dedupedByKey.values())
      .filter((item) => (item.relevanceScore || 0) > 0)
      .sort((a, b) => {
        const scoreDelta = (b.relevanceScore || 0) - (a.relevanceScore || 0);
        if (scoreDelta !== 0) return scoreDelta;

        const aTitle = normalizeForSearch(a.title);
        const bTitle = normalizeForSearch(b.title);
        const aStarts = aTitle.startsWith(searchTermLower);
        const bStarts = bTitle.startsWith(searchTermLower);

        if (aStarts && !bStarts) return -1;
        if (!aStarts && bStarts) return 1;
        return aTitle.localeCompare(bTitle);
      });

    // Limit results based on type
    const maxResults = type === 'all' ? 20 : 30;
    return rankedResults.slice(0, maxResults);
  } catch (error) {
    console.error('Error searching content:', error);
    return [];
  }
}